/**
 * 一覧での一括入力（2026-10-07）— Excel の表をコピーして貼り付け → 列を対応付け → メニューに当てる → まとめて下書き → まとめて申請。
 *
 * ⚠️ CSV ファイルではなく貼り付けを入口にする: 日本語の Excel は CSV を Shift_JIS で保存しがちで化ける。コピーした表は
 *    ブラウザがタブ区切りの文字列で受け取るので、文字コードの問題が起きない。
 * ⚠️ 当てたメニューは必ず目で確かめさせる（部分一致は「要確認」・外せる）。公式値を別の商品に付ける事故を防ぐ。
 * 1 回 300 行まで（サーバの上限 = src/routes/bulk.ts）。
 */
import { useMemo, useState } from "react";
import { VALUE_BASES } from "@kalori/business/brands";
import { api, type MenuMatch, type Nutrition, type NutritionKey } from "../api";
import { Link, navigate } from "../router";
import { NUTRITION_FIELDS, fmtNumber } from "../format";

type ColumnKey = "ignore" | "name" | NutritionKey | "servingGrams";
const COLUMN_LABEL: Record<ColumnKey, string> = {
  ignore: "使わない",
  name: "メニュー名",
  cal: "エネルギー",
  protein: "たんぱく質",
  fat: "脂質",
  carbs: "炭水化物",
  sugar: "糖質",
  fiber: "食物繊維",
  salt: "食塩相当量",
  servingGrams: "1 食の重量",
};

/** 見出しの言葉から列を推し量る（外れていたら画面で直せる） */
export function guessColumn(header: string): ColumnKey {
  const h = header.normalize("NFKC").toLowerCase().replace(/\s/g, "");
  if (/メニュー|商品名|品名|名称|name/.test(h)) return "name";
  if (/エネルギー|熱量|カロリー|kcal|energy/.test(h)) return "cal";
  if (/たんぱく|タンパク|蛋白|protein/.test(h)) return "protein";
  if (/脂質|脂肪|fat/.test(h)) return "fat";
  if (/糖質|sugar/.test(h)) return "sugar";
  if (/食物繊維|繊維|fiber/.test(h)) return "fiber";
  if (/炭水化物|carb/.test(h)) return "carbs";
  if (/食塩|塩分|salt/.test(h)) return "salt";
  if (/重量|内容量|グラム|weight/.test(h)) return "servingGrams";
  return "ignore";
}

/** タブ区切り（Excel の貼り付け）を行と列に。セルの中の改行（"..." で囲まれる）にも耐える */
export function parseTsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"' && cell === "") quoted = true;
    else if (ch === "\t") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(cell);
      cell = "";
      if (row.some((c) => c.trim() !== "")) rows.push(row);
      row = [];
    } else cell += ch;
  }
  row.push(cell);
  if (row.some((c) => c.trim() !== "")) rows.push(row);
  return rows;
}

function num(raw: string | undefined): number | null | undefined {
  if (raw == null) return null;
  const s = raw.normalize("NFKC").replace(/[,\s]|kcal|g$/gi, "");
  if (s === "" || s === "-" || s === "—") return null;
  if (!/^\d+(\.\d+)?$/.test(s)) return undefined;
  return Number(s);
}

interface Row {
  name: string;
  values: Record<NutritionKey, number | null>;
  servingGrams: number | null;
  invalid: boolean;
  match: MenuMatch | null | undefined;
  include: boolean;
}

export function BulkInput() {
  const [text, setText] = useState("");
  const [hasHeader, setHasHeader] = useState(true);
  const [columns, setColumns] = useState<ColumnKey[]>([]);
  const [unit, setUnit] = useState<"serving" | "per100">("serving");
  const [rows, setRows] = useState<Row[] | null>(null);
  const [valueBasis, setValueBasis] = useState("");
  const [basisNote, setBasisNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ created: number; updated: number; skipped: number } | null>(null);

  const table = useMemo(() => parseTsv(text), [text]);
  const header = hasHeader ? table[0] ?? [] : [];
  const body = hasHeader ? table.slice(1) : table;
  const width = Math.max(0, ...table.map((r) => r.length));

  const onPaste = (value: string) => {
    setText(value);
    setRows(null);
    setDone(null);
    const t = parseTsv(value);
    const w = Math.max(0, ...t.map((r) => r.length));
    setColumns(Array.from({ length: w }, (_, i) => (hasHeader ? guessColumn(t[0]?.[i] ?? "") : "ignore")));
  };

  /** 列の対応付けで値を読み、メニューに当てる */
  const prepare = async () => {
    setError(null);
    const nameCol = columns.indexOf("name");
    if (nameCol < 0) return setError("「メニュー名」の列を選んでください。");
    if (unit === "per100" && !columns.includes("servingGrams")) return setError("100g あたりの表は、「1 食の重量」の列が要ります（1 食あたりに換算します）。");
    const parsedRows = body.slice(0, 300).map((r) => {
      let invalid = false;
      const raw = {} as Record<NutritionKey, number | null>;
      for (const f of NUTRITION_FIELDS) {
        const col = columns.indexOf(f.key);
        const v = col < 0 ? null : num(r[col]);
        if (v === undefined) invalid = true;
        raw[f.key] = v ?? null;
      }
      const sgCol = columns.indexOf("servingGrams");
      const sg = sgCol < 0 ? null : num(r[sgCol]);
      if (sg === undefined) invalid = true;
      const servingGrams = sg ?? null;
      const values = {} as Record<NutritionKey, number | null>;
      for (const f of NUTRITION_FIELDS) {
        const v = raw[f.key];
        values[f.key] =
          unit === "serving" || v == null ? v : servingGrams == null ? null : f.key === "cal" ? Math.round((v * servingGrams) / 100) : Math.round((v * servingGrams) / 10) / 10;
      }
      return { name: (r[nameCol] ?? "").trim(), values, servingGrams, invalid };
    });
    setBusy(true);
    const m = await api.bulkMatch(parsedRows.map((r) => r.name || "-"));
    setBusy(false);
    if (!m.ok) return setError(m.error);
    setRows(parsedRows.map((r, i) => ({ ...r, match: m.data[i], include: !r.invalid && m.data[i]?.kind === "exact" })));
  };

  const save = async (submit: boolean) => {
    if (!rows) return;
    if (!valueBasis) return setError("値の根拠を選んでください（表の全部の行に付きます）。");
    const picked = rows.filter((r) => r.include && r.match);
    if (picked.length === 0) return setError("取り込む行を選んでください。");
    setBusy(true);
    setError(null);
    const r = await api.bulkEntries({
      valueBasis,
      basisNote: basisNote.trim() || null,
      rows: picked.map((p) => ({ productDbId: p.match!.productId, nutrition: p.values as Nutrition, servingGrams: p.servingGrams })),
    });
    if (!r.ok) {
      setBusy(false);
      return setError(r.error);
    }
    if (submit) {
      // 取り込んだ下書きをまとめて申請（規約への同意・検算は 1 件ずつの申請と同じ）
      const entries = await api.entries();
      const ids = entries.ok
        ? entries.data.filter((e) => e.reviewState === "editing" && picked.some((p) => p.match!.productId === e.productDbId)).map((e) => e.id)
        : [];
      if (ids.length > 0) {
        const s = await api.bulkSubmit(ids);
        if (!s.ok) setError(`下書きにしましたが、申請できませんでした: ${s.error}`);
        else if (s.data.submitted < ids.length) setError(`${ids.length - s.data.submitted} 件は申請できませんでした（利用規約への同意・値の根拠などを、各メニューの画面でご確認ください）。`);
      }
    }
    setBusy(false);
    setDone({ created: r.data.created, updated: r.data.updated, skipped: r.data.skipped.length });
  };

  return (
    <section className="stack-lg">
      <div>
        <p className="small">
          <Link href="/entries">← 公式値の入力</Link>
        </p>
        <h1>一覧で入力する</h1>
        <p className="muted">
          御社の栄養成分の一覧（Excel など）を、見出しの行ごとコピーして下に貼り付けてください。メニュー名で kalori の掲載中のメニューに当てて、まとめて下書きにします。
        </p>
      </div>

      <div className="card stack">
        <label className="field">
          <span>表を貼り付ける</span>
          <textarea rows={8} value={text} onChange={(e) => onPaste(e.target.value)} placeholder={"メニュー名\tエネルギー\tたんぱく質\t脂質\t炭水化物\t食塩相当量\n唐揚げ定食\t812\t30\t40\t80\t3.2"} />
        </label>
        <div className="toolbar">
          <label className="check-item">
            <input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />
            1 行目は見出し
          </label>
          <div className="segmented" role="group" aria-label="表の単位">
            <button type="button" className={unit === "serving" ? "on" : ""} onClick={() => setUnit("serving")}>
              1 食あたりの表
            </button>
            <button type="button" className={unit === "per100" ? "on" : ""} onClick={() => setUnit("per100")}>
              100g あたりの表
            </button>
          </div>
        </div>
        {width > 0 && (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  {Array.from({ length: width }, (_, i) => (
                    <th key={i}>
                      <select
                        value={columns[i] ?? "ignore"}
                        aria-label={`${i + 1} 列目`}
                        onChange={(e) => setColumns(columns.map((c, j) => (j === i ? (e.target.value as ColumnKey) : c)))}
                      >
                        {(Object.keys(COLUMN_LABEL) as ColumnKey[]).map((k) => (
                          <option key={k} value={k}>
                            {COLUMN_LABEL[k]}
                          </option>
                        ))}
                      </select>
                      {hasHeader && <div className="muted small">{header[i]}</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {body.slice(0, 3).map((r, i) => (
                  <tr key={i}>
                    {Array.from({ length: width }, (_, j) => (
                      <td key={j} className="small">
                        {r[j]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {body.length > 3 && <p className="muted small">ほか {body.length - 3} 行（1 回 300 行まで）</p>}
          </div>
        )}
        <p>
          <button className="btn primary" disabled={busy || width === 0} onClick={() => void prepare()}>
            {busy && !rows ? "メニューに当てています…" : "メニューに当てる"}
          </button>
        </p>
      </div>

      {rows && (
        <div className="card stack">
          <h2>確認</h2>
          <p className="muted small">
            「要確認」は名前が一部だけ一致したものです。違うメニューに当たっていないか確かめてから選んでください。当たらなかった行は、
            <Link href="/catalog">掲載中のメニュー</Link>から 1 件ずつ入力できます。
          </p>
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>取り込む</th>
                  <th>貼り付けた名前</th>
                  <th>当たったメニュー</th>
                  <th className="num">エネルギー</th>
                  <th className="num hide-sm">いまの掲載値</th>
                  <th className="num hide-sm">たんぱく質</th>
                  <th className="num hide-sm">脂質</th>
                  <th className="num hide-sm">炭水化物</th>
                  <th className="num hide-sm">食塩</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={r.include ? "" : "muted"}>
                    <td>
                      <input
                        type="checkbox"
                        checked={r.include}
                        disabled={!r.match || r.invalid}
                        aria-label={`${r.name} を取り込む`}
                        onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, include: e.target.checked } : x)))}
                      />
                    </td>
                    <td>{r.name}</td>
                    <td>
                      {r.invalid ? (
                        <span className="error-text small">数字でない値があります</span>
                      ) : r.match ? (
                        <>
                          {r.match.name} {r.match.kind === "partial" && <span className="chip state-catalog-leaving">要確認</span>}
                        </>
                      ) : (
                        <span className="muted small">当たりませんでした</span>
                      )}
                    </td>
                    <td className="num">{fmtNumber(r.values.cal, "kcal")}</td>
                    <td className="num hide-sm muted">{fmtNumber(r.match?.currentCal ?? null, "kcal")}</td>
                    <td className="num hide-sm">{fmtNumber(r.values.protein, "g")}</td>
                    <td className="num hide-sm">{fmtNumber(r.values.fat, "g")}</td>
                    <td className="num hide-sm">{fmtNumber(r.values.carbs, "g")}</td>
                    <td className="num hide-sm">{fmtNumber(r.values.salt, "g")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="toolbar">
            <label className="field">
              <span className="small">値の根拠（全部の行に付きます）</span>
              <select value={valueBasis} onChange={(e) => setValueBasis(e.target.value)}>
                <option value="">選んでください</option>
                {Object.entries(VALUE_BASES).map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="small">補足（任意）</span>
              <input value={basisNote} maxLength={500} onChange={(e) => setBasisNote(e.target.value)} placeholder="例: 2026 年 9 月の分析" />
            </label>
          </div>
          <div className="actions">
            <span className="muted small">{rows.filter((r) => r.include).length} 行を取り込みます。申請中のメニューは飛ばし、公開中のメニューは改訂の下書きになります。</span>
            <span className="spacer" />
            <button className="btn" disabled={busy} onClick={() => void save(false)}>
              下書きにする
            </button>
            <button className="btn primary" disabled={busy} onClick={() => void save(true)}>
              下書きにしてまとめて申請
            </button>
          </div>
        </div>
      )}

      {error && <p className="error">{error}</p>}
      {done && (
        <div className="notice">
          新しく {done.created} 件・上書き {done.updated} 件を下書きにしました{done.skipped > 0 && `（${done.skipped} 件は飛ばしました）`}。{" "}
          <button className="btn-link" onClick={() => navigate("/entries")}>
            公式値の入力へ
          </button>
        </div>
      )}
    </section>
  );
}
